import { BehaviorSubject, defer, from, interval, Observable, Subscription } from 'rxjs';
import User from '@/repositories/data/User';
import Resource from '@/repositories/Resource';
import { filter, map, tap } from 'rxjs/operators';
import UserRepository from '@/repositories/UserRepository';

export default class GlobalState {
    private readonly _userRepository: UserRepository;
    private readonly _toastSubject = new BehaviorSubject<string | null>(null);
    private _keepAliveSubscription: Subscription;
    private _currentUser: User | null = null;

    public constructor(userRepository: UserRepository) {
        this._userRepository = userRepository;
        this._keepAliveSubscription = interval(10 * 60 * 1000).subscribe(() => this._userRepository.keepAlive());
    }

    public generalErrorHandling(error: any): void {
        if (error && error.response) {
            if (error.response.status === 403) {
                // TODO: Review this handling
            }
        }
    }

    public toast(message: string): void {
        this._toastSubject.next(message);
    }

    public get currentToast(): string | null {
        return this._toastSubject.value;
    }

    public get toastObservable(): Observable<string | null> {
        return this._toastSubject.asObservable();
    }

    public get isUserLoaded(): boolean {
        return this._currentUser !== null;
    }

    public beginUserLogin(): void {
        window.location.href =
            (process.env.VUE_APP_API_ENDPOINT as string).replace('/api', '') + '/oauth2/authorization/ngc-auth';
    }

    public beginUserLogout(): void {
        this._userRepository.logout().subscribe((resource) => {
            if (resource.isSuccess && resource.data) {
                window.location.href = resource.data.logoutUrl;
            }
        });
    }

    public get userObservable(): Observable<Resource<User>> {
        // FIXME: If this is subscribed to while a findCurrent is ongoing, multiple calls are made (ReplaySubject?)
        return defer(() => {
            return this._currentUser
                ? from([Resource.success(this._currentUser)])
                : this._userRepository.findCurrent().pipe(
                      tap((userResource) => {
                          if (userResource.isSuccess) {
                              this._currentUser = userResource.data;
                          } else {
                              if (userResource.isError) {
                                  this.generalErrorHandling(userResource.error);
                              }
                              this._currentUser = null;
                          }
                      }),
                  );
        });
    }

    get verifiedUser(): Observable<User | null> {
        return this.userObservable.pipe(
            filter((t) => !t.isLoading),
            map((t) => (t.isSuccess && t.data ? t.data : null)),
        );
    }

    public get userIsNgcAdmin(): boolean {
        if (this._currentUser === null) {
            return false;
        } else {
            return this._currentUser.roles ? this._currentUser.roles.includes('ROLE_ADMIN') : false;
        }
    }

    public readInvitationToken(): string | null {
        return window.localStorage.getItem('invitationToken');
    }

    public saveInvitationToken(token: string): void {
        window.localStorage.setItem('invitationToken', token);
    }

    public clearInvitationToken(): void {
        window.localStorage.removeItem('invitationToken');
    }

    public get currentUser() {
        return this._currentUser;
    }
}
